import { createTheme } from "@mui/material/styles";

// ---------------------------------------------------------------------------
// Liquid Glass design system — Apple WWDC25 / iOS 26 / macOS Tahoe 26.
//
// Five must-haves to look like Liquid Glass (and not generic frosted glass):
//   1. backdrop-filter must include `saturate(180%)`
//   2. dual specular highlight (135° gradient + 1px white inner-rim shadow)
//   3. concentric corner radii (`inner = outer − padding`)
//   4. spring motion, not ease curves (response 0.3 / damping 0.6 for morphs)
//   5. content-adaptive tint (lightens/darkens against backdrop, not fixed)
//
// The brand stays — navy + antique gold are used as accent overlays at 14–22%
// against the glass surface, exactly the way Apple uses system tints.
// ---------------------------------------------------------------------------

const navy = {
  50: "#EEF1F7", 100: "#D6DCE9", 200: "#B0BBCF", 300: "#8499B5",
  400: "#5C7899", 500: "#3D5A80", 600: "#2C4468", 700: "#1B2A4A",
  800: "#152238", 900: "#0F1825", 950: "#0A1018",
};

const gold = {
  50: "#FBF8EF", 100: "#F9F3E3", 200: "#F0E4C3", 300: "#E3D09A",
  400: "#D4B96E", 500: "#B8962E", 600: "#997B24", 700: "#7A631D",
  800: "#5C4B16", 900: "#3D3210",
};

// Three material tiers with measured backdrop-filter + tint values.
// CHROME is the top-level navigation (header, sidebar); SURFACE is for cards,
// sheets, popovers; INSET is for interactive controls (buttons, chips). The
// strict cap is one primary glass per view — INSET stacks inside SURFACE,
// SURFACE inside CHROME, never deeper.
const glass = {
  chrome: {
    background: "rgba(255,255,255,0.55)",
    backdropFilter: "blur(30px) saturate(180%) brightness(1.05)",
    WebkitBackdropFilter: "blur(30px) saturate(180%) brightness(1.05)",
    border: "1px solid rgba(255,255,255,0.55)",
    boxShadow: [
      "inset 0 1px 0 rgba(255,255,255,0.65)", // top wet rim
      "0 8px 32px rgba(31,38,135,0.10)",      // outer ambient
      "0 1px 0 rgba(17,24,39,0.04)",          // bottom hairline
    ].join(", "),
  },
  surface: {
    background: "rgba(255,255,255,0.40)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    border: "1px solid rgba(255,255,255,0.50)",
    boxShadow: [
      "inset 0 1px 0 rgba(255,255,255,0.55)",
      "0 8px 32px rgba(31,38,135,0.08)",
    ].join(", "),
  },
  inset: {
    background: "rgba(255,255,255,0.18)",
    backdropFilter: "blur(12px) saturate(160%) brightness(1.05)",
    WebkitBackdropFilter: "blur(12px) saturate(160%) brightness(1.05)",
    border: "1px solid rgba(255,255,255,0.30)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.40)",
  },
  // Reusable specular-highlight gradient — applied to ::before of glass
  // elements to give the "wet rim" reading. Anchored at the 135° edge.
  specular:
    "linear-gradient(135deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.10) 18%, rgba(255,255,255,0) 45%, rgba(255,255,255,0) 85%, rgba(255,255,255,0.18) 100%)",
};

// Page-background gradient — gives the glass something interesting to refract.
// Without a varied background, blur(30) + saturate(180) renders nearly flat.
const pageGradient =
  "radial-gradient(ellipse 1200px 800px at 18% -10%, rgba(184,150,46,0.10) 0%, transparent 55%)," +
  "radial-gradient(ellipse 900px 600px at 92% 10%, rgba(27,42,74,0.10) 0%, transparent 55%)," +
  "radial-gradient(ellipse 1000px 700px at 50% 110%, rgba(184,150,46,0.06) 0%, transparent 55%)," +
  "linear-gradient(180deg, #F6F4EE 0%, #EFECE3 100%)";

// Spring motion — Apple's default morph + press timings, expressed as CSS
// cubic-beziers that approximate the SwiftUI .spring(response, damping)
// curves. Use these for transform/opacity transitions; not for layout.
const motion = {
  // spring(response: 0.3, dampingFraction: 0.6) — default morph
  morph: "300ms cubic-bezier(0.32, 0.72, 0, 1)",
  // spring(response: 0.25, dampingFraction: 0.32) — bouncy press
  press: "250ms cubic-bezier(0.34, 1.56, 0.64, 1)",
  // tint / focus fade
  tint: "180ms ease-out",
};

// SF Pro stack with Inter as fallback (Inter is metric-similar; system fonts
// take over on Apple devices where SF Pro is built in).
const sfStack =
  '"SF Pro Text", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Inter", "Helvetica Neue", system-ui, sans-serif';
const sfDisplay =
  '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Inter", "Helvetica Neue", system-ui, sans-serif';

const theme = createTheme({
  // Custom tokens (consumable via `theme.glass.*`, `theme.motion.*`).
  glass,
  motion,
  pageGradient,
  palette: {
    mode: "light",
    primary: { main: navy[700], light: navy[50], dark: navy[900] },
    secondary: { main: gold[500] },
    background: { default: "#F4F1EA", paper: "rgba(255,255,255,0.40)" },
    // Foreground vibrancy tiers — Apple's measured opacities against glass.
    text: {
      primary: "rgba(0,0,0,0.88)",
      secondary: "rgba(0,0,0,0.60)",
      disabled: "rgba(0,0,0,0.30)",
    },
    divider: "rgba(255,255,255,0.55)",
    success: { main: "#2D6A4F", light: "rgba(45,106,79,0.14)" },
    warning: { main: "#92400E", light: "rgba(146,64,14,0.14)" },
    error: { main: "#991B1B", light: "rgba(153,27,27,0.14)" },
    info: { main: navy[600], light: navy[50] },
  },
  // Concentric radii — outer surfaces use 22; inner surfaces compute their
  // own `outer − padding` value at render time. Default radius matches a
  // typical SURFACE tier card.
  shape: { borderRadius: 16 },
  spacing: 8,
  typography: {
    fontFamily: sfStack,
    fontSize: 13,
    // Liquid Glass type ramp. SF Pro Display for ≥20pt, SF Pro Text for <20pt.
    h1: {
      fontFamily: sfDisplay,
      fontSize: "2.125rem", // 34px (Large Title)
      fontWeight: 700,
      letterSpacing: "-0.031em", // -1.05 / 34
      lineHeight: 1.205,
      color: "rgba(0,0,0,0.88)",
    },
    h2: {
      fontFamily: sfDisplay,
      fontSize: "1.75rem", // 28px (Title 1)
      fontWeight: 700,
      letterSpacing: "-0.0286em",
      lineHeight: 1.214,
      color: "rgba(0,0,0,0.88)",
    },
    h3: {
      fontFamily: sfDisplay,
      fontSize: "1.375rem", // 22px (Title 2)
      fontWeight: 700,
      letterSpacing: "-0.0318em",
      lineHeight: 1.272,
      color: "rgba(0,0,0,0.88)",
    },
    h4: {
      fontFamily: sfDisplay,
      fontSize: "1.25rem", // 20px (Title 3)
      fontWeight: 600,
      letterSpacing: "-0.030em",
      lineHeight: 1.25,
      color: "rgba(0,0,0,0.88)",
    },
    h5: {
      fontFamily: sfStack,
      fontSize: "1.0625rem", // 17px (Headline)
      fontWeight: 600,
      letterSpacing: "-0.0253em",
      lineHeight: 1.294,
      color: "rgba(0,0,0,0.88)",
    },
    h6: {
      fontFamily: sfStack,
      fontSize: "0.9375rem", // 15px (Subhead, bold)
      fontWeight: 600,
      letterSpacing: 0,
      lineHeight: 1.266,
      color: "rgba(0,0,0,0.88)",
    },
    body1: {
      fontFamily: sfStack,
      fontSize: "1.0625rem", // 17px (Body)
      lineHeight: 1.294,
      letterSpacing: "-0.0253em",
      color: "rgba(0,0,0,0.88)",
    },
    body2: {
      fontFamily: sfStack,
      fontSize: "0.9375rem", // 15px (Subhead)
      lineHeight: 1.266,
      letterSpacing: 0,
      color: "rgba(0,0,0,0.60)",
    },
    subtitle1: {
      fontFamily: sfStack,
      fontSize: "1rem", // 16px (Callout)
      lineHeight: 1.25,
      letterSpacing: "-0.020em",
      color: "rgba(0,0,0,0.88)",
    },
    subtitle2: {
      fontFamily: sfStack,
      fontSize: "0.8125rem", // 13px (Footnote)
      lineHeight: 1.231,
      letterSpacing: "+0.0023em",
      color: "rgba(0,0,0,0.60)",
    },
    caption: {
      fontFamily: sfStack,
      fontSize: "0.75rem", // 12px (Caption 1)
      lineHeight: 1.25,
      letterSpacing: "+0.010em",
      color: "rgba(0,0,0,0.45)",
    },
    overline: {
      fontFamily: sfStack,
      fontSize: "0.6875rem", // 11px (Caption 2)
      lineHeight: 1.273,
      letterSpacing: "+0.064em",
      textTransform: "uppercase",
      fontWeight: 600,
      color: "rgba(0,0,0,0.45)",
    },
    button: {
      fontFamily: sfStack,
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: "-0.020em",
      fontSize: "1rem",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { background: "#F4F1EA" },
        body: {
          background: pageGradient,
          backgroundAttachment: "fixed",
          minHeight: "100vh",
        },
        // Smooth, light-touch scrollbars to match Apple's overlay scrollers.
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0,0,0,0.18)",
          borderRadius: 8,
          backgroundClip: "padding-box",
          border: "2px solid transparent",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "rgba(0,0,0,0.30)",
        },
        "*::-webkit-scrollbar-track": { background: "transparent" },
        // Keep existing animations for fade-in/up so the new look composes
        // with the existing animation usage across the app.
        "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.5 } },
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            animation: "none !important",
            transition: "none !important",
          },
        },
        "@media print": {
          "body, html": { background: "#FFFFFF !important" },
          "header, nav, aside, .MuiDrawer-root, .label-actions, .MuiTabs-root, .label-noprint": {
            display: "none !important",
          },
          ".label-print-area": {
            display: "block !important",
            "page-break-inside": "avoid",
          },
          ".MuiPaper-root": {
            boxShadow: "none !important",
            border: "1px solid #ccc !important",
            background: "#FFFFFF !important",
            backdropFilter: "none !important",
          },
        },
      },
    },
    // Paper = SURFACE tier glass by default.
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          background: glass.surface.background,
          backdropFilter: glass.surface.backdropFilter,
          WebkitBackdropFilter: glass.surface.WebkitBackdropFilter,
          border: glass.surface.border,
          borderRadius: 16,
          boxShadow: glass.surface.boxShadow,
          color: "rgba(0,0,0,0.88)",
          transition: `box-shadow ${motion.morph}, transform ${motion.morph}, background ${motion.tint}`,
          position: "relative",
          // Specular highlight along the top-left edge.
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background: glass.specular,
            opacity: 0.7,
            pointerEvents: "none",
            mixBlendMode: "soft-light",
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          background: glass.surface.background,
          backdropFilter: glass.surface.backdropFilter,
          WebkitBackdropFilter: glass.surface.WebkitBackdropFilter,
          border: glass.surface.border,
          borderRadius: 16,
          boxShadow: glass.surface.boxShadow,
          color: "rgba(0,0,0,0.88)",
          transition: `box-shadow ${motion.morph}, transform ${motion.morph}, background ${motion.tint}`,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background: glass.specular,
            opacity: 0.7,
            pointerEvents: "none",
            mixBlendMode: "soft-light",
          },
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: [
              "inset 0 1px 0 rgba(255,255,255,0.65)",
              "0 16px 40px rgba(31,38,135,0.14)",
            ].join(", "),
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: {
          borderRadius: 12, // concentric inset inside a 16-radius parent
          paddingInline: 16,
          height: 36,
          minWidth: 0,
          transition: `background ${motion.tint}, transform ${motion.press}, box-shadow ${motion.morph}`,
          "&:active": { transform: "scale(0.97)" },
        },
        containedPrimary: {
          backgroundColor: navy[700],
          color: "#ffffff",
          boxShadow: [
            "inset 0 1px 0 rgba(255,255,255,0.30)",
            "0 4px 14px rgba(27,42,74,0.30)",
          ].join(", "),
          "&:hover": { backgroundColor: navy[800] },
        },
        outlined: {
          background: glass.inset.background,
          backdropFilter: glass.inset.backdropFilter,
          WebkitBackdropFilter: glass.inset.WebkitBackdropFilter,
          border: glass.inset.border,
          color: navy[700],
          boxShadow: glass.inset.boxShadow,
          "&:hover": {
            background: "rgba(255,255,255,0.30)",
            borderColor: "rgba(255,255,255,0.55)",
          },
        },
        text: { color: navy[700], "&:hover": { background: "rgba(0,0,0,0.04)" } },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999, // Liquid Glass uses pill chips
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 26,
          background: glass.inset.background,
          backdropFilter: glass.inset.backdropFilter,
          WebkitBackdropFilter: glass.inset.WebkitBackdropFilter,
          border: glass.inset.border,
          color: "rgba(0,0,0,0.88)",
          letterSpacing: "-0.005em",
          "& .MuiChip-label": { paddingInline: 10 },
        },
        sizeSmall: { height: 22, fontSize: "0.6875rem", "& .MuiChip-label": { paddingInline: 8 } },
        outlined: { borderColor: "rgba(0,0,0,0.10)", color: navy[700] },
        filled: { backgroundColor: "rgba(27,42,74,0.10)", color: navy[700], border: "1px solid rgba(27,42,74,0.08)" },
        colorSuccess: { backgroundColor: "rgba(45,106,79,0.14)", color: "#1B4332", border: "1px solid rgba(45,106,79,0.22)" },
        colorWarning: { backgroundColor: "rgba(146,64,14,0.14)", color: "#5C2A06", border: "1px solid rgba(146,64,14,0.22)" },
        colorError: { backgroundColor: "rgba(153,27,27,0.14)", color: "#5C0F0F", border: "1px solid rgba(153,27,27,0.22)" },
      },
    },
    MuiIconButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          borderRadius: 999,
          color: "rgba(0,0,0,0.60)",
          transition: `background ${motion.tint}, transform ${motion.press}`,
          "&:hover": { background: "rgba(0,0,0,0.05)" },
          "&:active": { transform: "scale(0.92)" },
        },
      },
    },
    MuiListItemButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: `background ${motion.tint}`,
          "&:hover": { backgroundColor: "rgba(255,255,255,0.45)" },
          "&.Mui-selected": {
            backgroundColor: "rgba(255,255,255,0.55)",
            backdropFilter: "blur(8px) saturate(160%)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: glass.inset.background,
          backdropFilter: glass.inset.backdropFilter,
          WebkitBackdropFilter: glass.inset.WebkitBackdropFilter,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.10)" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.18)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: navy[600], borderWidth: 1.5 },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: "rgba(20,20,22,0.78)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          color: "rgba(255,255,255,0.96)",
          borderRadius: 12,
          fontSize: "0.8125rem",
          padding: "10px 14px",
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.30)",
          "& .MuiTypography-root": { color: "rgba(255,255,255,0.96)" },
        },
        arrow: { color: "rgba(20,20,22,0.78)" },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: glass.chrome.background,
          backdropFilter: glass.chrome.backdropFilter,
          WebkitBackdropFilter: glass.chrome.WebkitBackdropFilter,
          borderRight: glass.chrome.border,
          boxShadow: glass.chrome.boxShadow,
          // Edge-attached panel — no rounded corners (it sits flush against
          // the screen edge). Override the MuiPaper base radius and disable
          // the inherited ::before specular so it doesn't stack with the
          // Sidebar's own specular highlight.
          borderRadius: 0,
          "&::before": { display: "none" },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: "rgba(0,0,0,0.06)",
          fontSize: "0.9375rem",
          paddingBlock: 10,
          paddingInline: 14,
          color: "rgba(0,0,0,0.88)",
        },
        head: {
          fontWeight: 600,
          color: "rgba(0,0,0,0.45)",
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "+0.040em",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: { root: { backgroundColor: navy[700], color: "#ffffff" } },
    },
  },
});

export default theme;
